import { create } from "zustand";

/**
 * Auth Store
 *
 * Minimal store that tracks Clerk auth state.
 * Most auth functionality is handled by Clerk's hooks directly.
 * This store exists for compatibility with components that need auth state outside of React.
 */

type AuthStatus = "loading" | "authenticated" | "unauthenticated";

interface AuthUser {
  id: string;
  email?: string;
}

interface AuthState {
  status: AuthStatus;
  userId: string | null;
  user: AuthUser | null;
  error: string | null;

  // Actions
  setAuth: (
    status: AuthStatus,
    userId: string | null,
    user: AuthUser | null,
  ) => void;
  setError: (error: string | null) => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  status: "loading",
  userId: null,
  user: null,
  error: null,

  setAuth: (status, userId, user) => {
    set({ status, userId, user, error: null });
  },

  setError: (error) => {
    set({ error });
  },
}));

/**
 * Initialize auth state from Clerk.
 * Called from ClerkAuthSync component in App.tsx.
 */
export function syncAuthState(
  isLoaded: boolean,
  isSignedIn: boolean,
  userId: string | null,
  email?: string | null,
) {
  if (!isLoaded) {
    useAuthStore.setState({ status: "loading", userId: null, user: null });
  } else if (isSignedIn && userId) {
    useAuthStore.setState({
      status: "authenticated",
      userId,
      user: { id: userId, email: email || undefined },
    });
  } else {
    useAuthStore.setState({
      status: "unauthenticated",
      userId: null,
      user: null,
    });
  }
}
